// Mirrors backend/src/config/plans.js entitlement vocabulary. The frontend trusts
// the API-provided `entitlements` object (from GET /workspaces/:id) for UX gating;
// the backend remains authoritative.

export type FeatureKey =
  | 'integrations'
  | 'analytics_plus'
  | 'analytics_full'
  | 'templates_custom'
  | 'dedicated_success'
  | 'api_access';

export type LimitKey = 'contacts' | 'teamMembers' | 'campaigns';

export type BillingInterval = 'month' | 'quarter' | 'semiannual' | 'year';

// Billing durations a plan can renew on. Mirrors backend BILLING_INTERVALS; the
// semantic value is stored in Plan.interval and translated to Stripe's
// { interval, interval_count } server-side. Drives the admin interval dropdown.
export const BILLING_INTERVAL_OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-annually' },
  { value: 'year', label: 'Yearly' },
];

// year reuses the existing pricing.annual key; the others use pricing.* keys added alongside it.
const INTERVAL_I18N_KEY: Record<string, string> = {
  month: 'pricing.monthly',
  quarter: 'pricing.quarterly',
  semiannual: 'pricing.semiannual',
  year: 'pricing.annual',
};

const INTERVAL_LABEL_EN: Record<string, string> = {
  month: 'Monthly',
  quarter: 'Quarterly',
  semiannual: 'Semi-annually',
  year: 'Yearly',
};

// Human-readable billing-cycle label for a plan interval. Pass i18n's `t` for a
// localized label; falls back to English (and to the raw value for unknown intervals).
export function billingIntervalLabel(interval: string, t?: (k: string) => string): string {
  const key = INTERVAL_I18N_KEY[interval];
  if (t && key) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return INTERVAL_LABEL_EN[interval] ?? interval;
}

// Months each billing interval spans — mirrors backend intervalMonths. Used to
// normalize prices across intervals when labeling a plan change up/down.
const INTERVAL_MONTHS: Record<string, number> = { month: 1, quarter: 3, semiannual: 6, year: 12 };

export function intervalMonths(interval: string): number {
  return INTERVAL_MONTHS[interval] ?? 1;
}

// Normalized monthly price, or null for custom / "contact us" plans.
export function monthlyEquivalent(plan: { price: number | null; interval: string } | null | undefined): number | null {
  if (!plan || plan.price == null) return null;
  return plan.price / intervalMonths(plan.interval);
}

// Cosmetic classification for CTA labels only — the backend decides the real
// behavior (immediate upgrade vs. deferred downgrade).
export function classifyChange(
  current: { key: string; price: number | null; interval: string } | null | undefined,
  target: { key: string; price: number | null; interval: string },
): 'upgrade' | 'downgrade' | 'current' {
  if (current && current.key === target.key) return 'current';
  const cur = monthlyEquivalent(current);
  const next = monthlyEquivalent(target);
  if (cur == null || next == null) return 'upgrade';
  return next >= cur ? 'upgrade' : 'downgrade';
}

export interface Entitlements {
  planKey: string | null;
  features: string[];
  limits: Partial<Record<LimitKey, number | null>>;
}

// True if the entitlements include the feature ('*' = super-admin wildcard).
export function hasFeature(entitlements: Entitlements | null | undefined, key: FeatureKey): boolean {
  const list = entitlements?.features;
  return !!list && (list.includes('*') || list.includes(key));
}

// null/undefined = unlimited.
export function getLimit(entitlements: Entitlements | null | undefined, key: LimitKey): number | null {
  const v = entitlements?.limits?.[key];
  return v === undefined ? null : (v ?? null);
}
